import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import { getFollowContextMessageIds } from '@/utils/modalContextMessage';
import { getImageUrl } from '@/utils/urls';
import { getItems } from '@/redux/modules/item';
import { getModalContext } from '@/redux/modules/modal';
import getSearchColumns from '@/pages/Search/helpers/getSearchColumns';
import { getSearchedAndPromotedItems } from '@/redux/modules/search';
import { getWindowSize } from '@/redux/modules/windowSize';
import ImageWithFallback from '@liveauctioneers/caterwaul-components/lib/ImageWithFallback/ImageWithFallback';
import isEqual from 'react-fast-compare';
import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import Modal from '@liveauctioneers/caterwaul-components/lib/Modal/Modal';
import ModalFooter from '@liveauctioneers/caterwaul-components/lib/Modal/ModalFooter';
import ModalHeader from '@liveauctioneers/caterwaul-components/lib/Modal/ModalHeader';
import { parseQueryParams } from '@/utils/queryParams';
import SignUpEmail from './SignUpEmail';
import styled from 'styled-components';
import Text from '@liveauctioneers/caterwaul-components/lib/Text/Text';
import { useLocation } from 'react-router';
import { useSelector } from 'react-redux';

type Props = {
    bidderExists: boolean;
    cancelSignUp: (event: any) => void;
    error: string;
    openLoginModal: Function;
    setEmail: Function;
    show: boolean;
    submitCreateAccount: Function;
    submitted: boolean;
    whiteLabel: boolean;
};

const FollowSearchContextSignUpModal = (props: Props) => {
    const {
        bidderExists,
        cancelSignUp,
        error = '',
        openLoginModal,
        setEmail,
        show,
        submitCreateAccount,
        submitted = false,
    } = props;

    const { type: contextType } = useSelector(getModalContext, isEqual);
    const { width } = useSelector(getWindowSize, isEqual);
    const columns = getSearchColumns(width);
    const searchPageItemIds = useSelector((state) => getSearchedAndPromotedItems(state, columns), isEqual);
    const items = useSelector((state) => getItems(state, searchPageItemIds.slice(0, 3)), isEqual);
    const mapItems = React.useCallback((item, index) => {
        const imageUrl = getImageUrl({
            catalogId: item.catalogId,
            imageNumber: item.photos[0],
            imageVersion: item.imageVersion,
            itemId: item.itemId,
            sellerId: item.sellerId,
            width: 310,
        });
        return <StyledImageWithFallback alt="item1" key={index} src={imageUrl} />;
    }, []);
    const images = items.map(mapItems);

    const location = useLocation();
    const queryParams: any = parseQueryParams(location.search);
    const searchTerm = queryParams.keyword || '';

    const sellerLogoImage = Boolean(items) ? (
        <ImagesWrapper>{images}</ImagesWrapper>
    ) : (
        <StyledImageWithFallback alt="Item" forceFallback />
    );

    const [headerMessageId, blurbMessageId] = getFollowContextMessageIds(contextType);

    const headerContent = (
        <StyledHeader>
            {sellerLogoImage}
            <Text h2>
                <FormattedMessage id={headerMessageId} />
            </Text>
        </StyledHeader>
    );

    const modalHeader = <ModalHeader onClose={cancelSignUp} title={headerContent} />;

    const modalBody = (
        <BodyWrapper>
            <StyledBlurbText secondary>
                <FormattedMessage id={blurbMessageId} values={{ name: searchTerm }} />
            </StyledBlurbText>
            <SignUpEmail
                bidderExists={bidderExists}
                error={error}
                noBodyMargins
                setEmail={setEmail}
                submitCreateAccount={submitCreateAccount}
                submitted={submitted}
            />
        </BodyWrapper>
    );

    const modalFooter = (
        <ModalFooter>
            <StyledFooterText>
                <FormattedMessage
                    id="signUpModal.footer"
                    values={{
                        link: (
                            <Link key="link" onClick={openLoginModal}>
                                <StyledLinkText>
                                    <FormattedMessage id="signUpModal.footerLink" />
                                </StyledLinkText>
                            </Link>
                        ),
                    }}
                />
            </StyledFooterText>
        </ModalFooter>
    );

    return (
        <StyledModal
            body={modalBody}
            contentLabel="SignUpModal"
            footer={modalFooter}
            header={modalHeader}
            show={show}
        />
    );
};

export default React.memo<Props>(FollowSearchContextSignUpModal);

const StyledModal = styled(Modal).attrs(() => ({
    bodyClassName: 'bodyClassName',
}))`
    .bodyClassName {
        padding: 16px 0 20px 0;
        margin: 0 auto;
        width: 100%;
        max-width: 400px;

        @media (max-width: 767px) {
            padding: 15px 20px 40px;
        }
    }
`;

const StyledHeader = styled.div`
    margin-top: -41px;
`;

const ImagesWrapper = styled.div`
    display: flex;
    justify-content: center;
`;

const StyledImageWithFallback = styled(ImageWithFallback)`
    max-height: 74px;
    max-width: 100%;
    margin-bottom: 25px;
    margin-right: 1px;
    margin-left: 1px;
`;

const StyledBlurbText = styled(Text)`
    text-align: center;
    margin-bottom: 36px;
    display: block;
    & + div {
        padding: 0;
    }
`;

const BodyWrapper = styled.div``;

const StyledFooterText = styled(Text)`
    font-family: ${({ theme }) => theme.typography.fontPrimary};
    font-size: 14px;
    line-height: 20px;
    text-align: center;
    letter-spacing: 0.5px;
`;

const StyledLinkText = styled(StyledFooterText)`
    font-family: ${({ theme }) => theme.typography.fontPrimaryStrong};
    color: ${({ theme }) => theme.colors.blue100};
`;
